<template>
    <div>
        <b-row>
            <b-col md="8" lg="8">
                <b-form-group
                    :label="productNameLabel"
                    label-for="name"
                >
                    <b-form-input
                        id="name"
                        class="input-bg-warning"
                        v-model="name"
                        :state="nameState"
                        @input="emit"
                    />
                </b-form-group>
            </b-col>
            <b-col>
                <h6>{{ $t('Page(ReceivedRequest):Component(Text):product-name-in-request') }}</h6>
                <span>{{ customerProduct.name }}</span>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="8" lg="8">
                <b-form-group
                    :label="productIdLabel"
                    label-for="id"
                >
                    <b-form-input
                        id="id"
                        v-model="id"
                        @input="emit"
                    />
                </b-form-group>
            </b-col>
            <b-col>
                <h6>{{ $t('Page(ReceivedRequest):Component(Text):product-id-in-request') }}</h6>
                <span>{{ customerProduct.identifier }}</span>
            </b-col>
        </b-row>
        <b-row v-if="showProductWeight">
            <b-col md="8" lg="8">
                <b-form-group
                    :label="$t('Component(Placeholder):product-weight')"
                    label-for="productWeight"
                >
                    <b-form-input
                        type="number"
                        class="input-bg-warning"
                        id="productWeight"
                        :placeholder="$t('Component(Placeholder):product-weight')"
                        v-model="weight"
                        @input="emitAttributesChange"
                    />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <div class="demo-inline-spacing text-center">
                    <div>
                        {{ $t('Page(ReceivedRequest):Component(Text):is-purchased-from-supplier') }}
                    </div>
                    <b-form-radio
                        name="is-purchased"
                        :value="typePurchased"
                        v-model="type"
                        @change="onTypeChange"
                    >
                        {{ $t('Component(Choice):yes') }}
                    </b-form-radio>
                    <b-form-radio
                        name="is-purchased"
                        :disabled="isOwnProductTypeChangeDisabled"
                        :value="typeOwn"
                        v-model="type"
                        @change="onTypeChange"
                    >
                        {{ $t('Component(Choice):no') }}
                    </b-form-radio>
                    <InstructionInfoButton
                        class="pl-1"
                        :instruction="$t('Page(ReceivedRequest):Component(IButton):received-request-purchase-from-supplier-instruction')"
                    />
                </div>
            </b-col>
        </b-row>
        <Transition name="fade">
            <b-row v-if="type === typePurchased" class="mt-2">
                <b-col>
                    <SupplierSelector
                        border-variant="default"
                        :selected="product.suppliers"
                        @change="manageSupplier"
                    >
                        <template #under-heading>
                            <InstructionText class="m-1 simple-flow-instruction">
                                {{ $t('Page(ReceivedRequest):Component(Instruction):supplier-information') }}
                            </InstructionText>
                        </template>
                    </SupplierSelector>
                </b-col>
            </b-row>
        </Transition>
        <Transition name="fade">
            <b-row v-if="type === typePurchased && hasSupplier" class="mt-2">
                <b-col>
                    <b-form-group
                        :label="supplierNameLabel"
                        label-for="supplier-name"
                    >
                        <b-form-input
                            id="supplier-name"
                            class="input-bg-warning"
                            v-model="supplierName"
                            @input="emit"
                        />
                    </b-form-group>

                    <b-form-group
                        :label="supplierIdLabel"
                        label-for="supplier-id"
                    >
                        <b-form-input
                            id="supplier-id"
                            v-model="supplierId"
                            @input="emit"
                        />
                    </b-form-group>

                    <b-form-group
                        :label="suppliersScipNumberLabel"
                        label-for="suppliers-scip-number"
                    >
                        <b-form-input
                            id="suppliers-scip-number"
                            v-model="suppliersScipNumberId"
                            @input="emit"
                        />
                    </b-form-group>

                </b-col>
            </b-row>
        </Transition>
        <b-row v-if="isComplexQuestionVisible">
            <b-col>
                <div class="demo-inline-spacing text-center">
                    <div>
                        {{ $t('Component(ReceivedRequestConcernElements):is-complex-product') }}
                    </div>
                    <b-form-radio
                        name="is-complex-product"
                        :value="true"
                        v-model="isComplex"
                        :disabled="includedSubstances.length > 0"
                        @change="onIsComplexChange"
                    >
                        {{ $t('Component(Choice):yes') }}
                    </b-form-radio>
                    <b-form-radio
                        name="is-complex-product"
                        :value="false"
                        v-model="isComplex"
                        :disabled="!isAbleToSwitchToSimpleProduct"
                        @change="onIsComplexChange"
                    >
                        {{ $t('Component(Choice):no') }}
                    </b-form-radio>
                    <InstructionInfoButton
                        class="pl-1"
                        :instruction="$t('Page(ReceivedRequest):Component(IButton):received-request-is-complex-instruction')"
                    />
                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {BFormInput, BFormGroup, BCol, BRow, BBadge, BFormRadio} from 'bootstrap-vue'
import ReplyTabProductSection from "@core/mixins/ui/reply-tab-product-section";
import ProductCategoryTagView from "@core/components/product/ProductCategoryTagView";
import {setNameOrId, ProductNames, ProductIdentifiers, ProductType} from "@/libs/product";
import {SupplierSelector} from "@core/components/supply-chain";
import {ProductProvider} from "@core/domain/product";
import {Button} from "@core/components/common";
import InstructionInfoButton from "@core/components/common/instructions/InstructionInfoButton.vue";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import {LegislationApi} from "@core/api";
import {InformationLevel} from "@core/domain/legislation";

export default {
    mixins: [ReplyTabProductSection],
    components: {
        InstructionText,
        InstructionInfoButton,
        BFormInput, BFormGroup, BCol, BRow, ProductCategoryTagView, BBadge, BFormRadio, SupplierSelector, Button,
    },
    props: {
        product: {
            required: true,
        },
        request: {
            required: true,
        },
        customerProduct: {
            required: true
        },
        includedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        showProductWeight: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            name: this.getProductName(this.product.names, this.resolveProductNameKey()),
            id: this.getProductId(this.product.identifiers, this.resolveProductIdentifierKey()),

            supplierName: this.getProductName(this.product.names, ProductNames.SUPPLIERS_PRODUCT_NAME),
            supplierId: this.getProductId(this.product.identifiers, ProductIdentifiers.SUPPLIERS_PRODUCT_ID),
            suppliersScipNumberId: this.getProductId(this.product.identifiers, ProductIdentifiers.SUPPLIERS_SCIP_NUMBER),

            selected: [
                { title: 'Square' },
                { title: 'Romboid' },
            ],
            isComplex: null,
            type: ProductType.UNDEFINED,
            initialProductType: null,
            typeOwn: ProductType.OWN,
            typePurchased: ProductType.PURCHASED_UNDEFINED_SUPPLIER,

            weight: null,

            hierarchy: {children: []},
        };
    },
    methods: {
        manageSupplier(supplierIds, suppliers) {
            this.$emit('changeSupplier', supplierIds, suppliers);
        },
        onTypeChange(type) {
            if (type === ProductType.PURCHASED_UNDEFINED_SUPPLIER && (this.hasSupplier || this.initialProductType === ProductType.PURCHASED)) {
                type = ProductType.PURCHASED;
            }

            this.$emit('productTypeChanged', type, () => {}, () => {
                //  failed callback
                if (type === ProductType.OWN) {
                    this.type = ProductType.PURCHASED;
                }
            });

            if ((type === ProductType.PURCHASED || type === ProductType.PURCHASED_UNDEFINED_SUPPLIER) && this.isComplex) {
                ProductProvider(this.product.id)
                    .getHierarchy()
                    .then((h) => {
                        if (h.children.length === 0) {
                            this.isComplex = null;
                            this.onIsComplexChange();
                        }
                    });
            }
            this.emitValidity();
        },
        categoryModalClosed() {
            this.emit();
            this.emitValidity();
        },
        afterCategoryRemoved(categories) {
            this.product.categories = categories;
        },
        onCategoryChange({idList, items}) {
            this.product.categories = items.map(item => item.category);
        },
        emitAttributesChange() {
            this.$emit('characteristicsChange', {
                weight: parseFloat(this.weight)
            })
        },
        emit() {
            setNameOrId(
                this.product.names,
                this.resolveProductNameKey(),
                this.name
            );
            setNameOrId(
                this.product.identifiers,
                this.resolveProductIdentifierKey(),
                this.id
            );

            if (this.type === ProductType.PURCHASED) {
                setNameOrId(
                    this.product.names,
                    ProductNames.SUPPLIERS_PRODUCT_NAME,
                    this.supplierName
                );
                setNameOrId(
                    this.product.identifiers,
                    ProductIdentifiers.SUPPLIERS_PRODUCT_ID,
                    this.supplierId
                );
                setNameOrId(
                    this.product.identifiers,
                    ProductIdentifiers.SUPPLIERS_SCIP_NUMBER,
                    this.suppliersScipNumberId
                );
            }

            this.$emit('change');
            this.emitValidity();
        },

        resolveProductNameKey() {
            return ProductNames.OUR_PRODUCT_NAME;
        },

        resolveProductIdentifierKey() {
            return ProductIdentifiers.PRODUCT_ID;
        },

        onIsComplexChange() {
            this.$emit('isComplex', this.isComplex);
            this.emitValidity();
        },

        isInformationValid() {
            if (!this.name) {
                return false;
            }

            if (this.type === ProductType.UNDEFINED) {
                return false;
            }

            if (this.type === ProductType.PURCHASED_UNDEFINED_SUPPLIER) {
                return this.isComplex !== null;
            }

            if (this.type === ProductType.PURCHASED) {
                return this.product.suppliers.length > 0 && this.supplierName && null !== this.isComplex;
            }

            return !(this.type === ProductType.OWN && null === this.isComplex);
        },

        emitValidity() {
            this.$emit('validityChanged', this.isInformationValid());
        },

        fetchHierarchy() {
            ProductProvider(this.product.id)
                .getHierarchy()
                .then((h) => {
                    this.hierarchy = h;
                });
        },

        populateNameAndIdentifier() {
            this.name = this.getProductName(this.product.names, this.resolveProductNameKey());
            this.id = this.getProductId(this.product.identifiers, this.resolveProductIdentifierKey());

            if (this.type === ProductType.PURCHASED) {
                this.supplierName = this.getProductName(this.product.names, ProductNames.SUPPLIERS_PRODUCT_NAME);
                this.supplierId = this.getProductId(this.product.identifiers, ProductIdentifiers.SUPPLIERS_PRODUCT_ID);
                this.suppliersScipNumberId = this.getProductId(this.product.identifiers, ProductIdentifiers.SUPPLIERS_SCIP_NUMBER);
            }
        },

        setInitialProductType(type) {
            if (this.initialProductType === null) {
                this.initialProductType = type;
            }
        }
    },
    computed: {
        nameState() {
            return this.name !== undefined && this.name !== '' && this.name.length > 1;
        },
        idState() {
            return this.id !== undefined && this.id !== '' && this.id.length > 1;
        },
        isAbleToSwitchToSimpleProduct() {
            return this.hierarchy.children.length === 0;
        },

        productNameLabel() {
            return this.getNameLabel(this.resolveProductNameKey());
        },

        productIdLabel() {
            return this.getIdentifierLabel(this.resolveProductIdentifierKey());
        },

        supplierNameLabel() {
            return this.getNameLabel(ProductNames.SUPPLIERS_PRODUCT_NAME);
        },

        supplierIdLabel() {
            return this.getIdentifierLabel(ProductIdentifiers.SUPPLIERS_PRODUCT_ID);
        },

        suppliersScipNumberLabel() {
            return this.getIdentifierLabel(ProductIdentifiers.SUPPLIERS_SCIP_NUMBER);
        },

        hasSupplier() {
            return this.product.suppliers.length > 0;
        },

        suppliers() {
            return this.product.suppliers;
        },

        productType() {
            return this.product.type;
        },

        isOwnProductTypeChangeDisabled() {
            //  HAM-999
            return false;
            // return ProductType.PURCHASED === this.productType;
        },
        isComplexQuestionVisible() {
            const typesThatQuestionHasToBeHidden = [
                ProductType.PURCHASED,
                ProductType.PURCHASED_UNDEFINED_SUPPLIER,
                ProductType.UNDEFINED,
            ];
            return !typesThatQuestionHasToBeHidden.includes(this.type);
        }
    },

    watch: {
        suppliers() {
            if (this.hasSupplier && this.type === ProductType.PURCHASED_UNDEFINED_SUPPLIER) {
                this.onTypeChange(ProductType.PURCHASED);
                this.type = ProductType.PURCHASED;
                this.typePurchased = ProductType.PURCHASED;
            }
        },
        productType() {
            this.populateNameAndIdentifier();
            this.setInitialProductType(this.productType);
        },
        product(p) {
            this.type = this.product.type;

            if (this.hasSupplier) {
                this.typePurchased = ProductType.PURCHASED
            }

            this.weight = this.product.characteristics.weight;
            this.populateNameAndIdentifier();
            this.emitValidity();
        },
        request() {
            this.isComplex = this.request.isComplexProduct;
            this.emitValidity();
        }
    }
}
</script>
